<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <cytomine-modal 
    :active="active" 
    :title="$t('annotation-export')" 
    @close="close"
    :width="700"
  >
    <div v-if="!exporting">
      <b-message type="is-info" has-icon icon-size="is-small">
        <p>{{ $t('annotation-export-info') }}</p>
      </b-message>

      <div class="export-options">
        <b-field :label="$t('export-scope')">
          <b-radio 
            v-model="exportScope" 
            native-value="single"
            @input="onScopeChange"
          >
            {{ $t('single-image') }}
          </b-radio>
          <b-radio 
            v-model="exportScope" 
            native-value="project"
            @input="onScopeChange"
          >
            {{ $t('entire-project') }}
          </b-radio>
        </b-field>

        <b-field 
          v-if="exportScope === 'single'" 
          :label="$t('select-image')"
        >
          <cytomine-multiselect
            v-model="selectedImages"
            :options="images"
            :label="blindMode ? 'blindedName' : 'instanceFilename'"
            track-by="id"
            :multiple="false"
            :searchable="true"
            :placeholder="$t('select-image-placeholder')"
          />
        </b-field>

        <b-field :label="$t('annotation-types')">
          <b-checkbox v-model="includeUserAnnotations">
            {{ $t('user-annotations') }}
          </b-checkbox>
          <b-checkbox v-model="includeAlgorithmAnnotations">
            {{ $t('algorithm-annotations') }}
          </b-checkbox>
        </b-field>

        <b-field 
          v-if="includeAlgorithmAnnotations" 
          :label="$t('algorithm-jobs')"
        >
          <cytomine-multiselect
            v-model="selectedAlgorithmJobs"
            :options="algorithmJobs"
            label="fullName"
            track-by="id"
            :multiple="true"
            :searchable="true"
            :placeholder="$t('all-algorithm-jobs')"
          />
        </b-field>

        <b-field :label="$t('terms-filter')">
          <b-checkbox v-model="filterByTerms">
            {{ $t('filter-by-terms') }}
          </b-checkbox>
        </b-field>

        <b-field 
          v-if="filterByTerms" 
          :label="$t('select-terms')"
        >
          <ontology-tree-multiselect
            v-model="selectedTerms"
            :ontology="ontology"
            :multiple="true"
            :placeholder="$t('all-terms')"
          />
        </b-field>
      </div>
    </div>

    <div v-else class="export-progress">
      <b-loading :active="true" :is-full-page="false"></b-loading>
      <p class="has-text-centered">{{ $t('exporting-annotations') }}...</p>
    </div>

    <template #footer>
      <button
        class="button"
        type="button"
        @click="close"
        :disabled="exporting"
      >
        {{ $t('button-cancel') }}
      </button>
      <button
        class="button is-primary"
        :class="{ 'is-loading': exporting }"
        :disabled="!canExport || exporting"
        @click="exportAnnotations"
      >
        {{ $t('button-export') }}
      </button>
    </template>
  </cytomine-modal>
</template>

<script>
import CytomineModal from '@/components/utils/CytomineModal';
import CytomineMultiselect from '@/components/form/CytomineMultiselect';
import OntologyTreeMultiselect from '@/components/ontology/OntologyTreeMultiselect';
import { get } from '@/utils/store-helpers';
import { ImageInstanceCollection, UserJobCollection } from 'cytomine-client';
import { fullName } from '@/utils/user-utils.js';
import axios from 'axios';

export default {
  name: 'annotation-export-modal',
  components: {
    CytomineModal,
    CytomineMultiselect,
    OntologyTreeMultiselect
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      exportScope: 'project',
      selectedImages: null, // Single object, not array
      includeUserAnnotations: true,
      includeAlgorithmAnnotations: true,
      selectedAlgorithmJobs: [],
      filterByTerms: false,
      selectedTerms: [],
      exporting: false,
      images: [],
      algorithmJobs: []
    };
  },
  computed: {
    project: get('currentProject/project'),
    ontology: get('currentProject/ontology'),
    blindMode() {
      return this.project.blindMode;
    },
    canExport() {
      // Must have at least one annotation type selected
      const hasAnnotationTypes = this.includeUserAnnotations || this.includeAlgorithmAnnotations;
      
      if (this.exportScope === 'single') {
        // For single image mode, must also have an image selected
        // selectedImages is a single object when multiple=false, not an array
        return this.selectedImages && hasAnnotationTypes;
      }
      
      // For project mode, only need annotation types
      return hasAnnotationTypes;
    }
  },
  methods: {
    close() {
      if (!this.exporting) {
        this.resetForm();
        this.$emit('close');
      }
    },

    resetForm() {
      this.exportScope = 'single';
      this.selectedImages = null;
      this.includeUserAnnotations = true;
      this.includeAlgorithmAnnotations = true;
      this.selectedAlgorithmJobs = [];
      this.filterByTerms = false;
      this.selectedTerms = [];
    },

    onScopeChange() {
      if (this.exportScope === 'project') {
        this.selectedImages = null;
      }
    },

    async fetchImages() {
      try {
        this.images = (await ImageInstanceCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.project.id,
          light: true
        })).array;
      }
      catch (error) {
        console.error('Error fetching images:', error);
        this.$notify({
          type: 'error',
          text: this.$t('error-loading-images')
        });
      }
    },

    async fetchAlgorithmJobs() {
      try {
        this.algorithmJobs = (await UserJobCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.project.id
        })).array;
        this.algorithmJobs.forEach(job => {
          job.fullName = fullName(job);
        });
      }
      catch (error) {
        console.error('Error fetching algorithm jobs:', error);
        this.$notify({
          type: 'error',
          text: this.$t('error-loading-algorithm-jobs')
        });
      }
    },

    async exportAnnotations() {
      if (!this.canExport) return;

      this.exporting = true;

      try {
        const formData = new FormData();

        formData.append('project_id', this.project.id);
        formData.append('include_user_annotations', this.includeUserAnnotations);
        formData.append('include_algorithm_annotations', this.includeAlgorithmAnnotations);

        if (this.exportScope === 'single' && this.selectedImages) {
          formData.append('image_ids', this.selectedImages.id);
        }

        if (this.filterByTerms && this.selectedTerms.length > 0) {
          formData.append('term_ids', this.selectedTerms);
        }

        if (this.includeAlgorithmAnnotations && this.selectedAlgorithmJobs.length > 0) {
          formData.append('algorithm_job_ids', this.selectedAlgorithmJobs.map(job => job.id));
        }

        const response = await axios.post('/annotations/export', 
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            responseType: 'blob'
          }
        );

        // Create download link
        const blob = new Blob([response.data], {type:'application/zip'});
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        
        const filename = `project_${this.project.id}_annotations.zip`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(href);

        this.$notify({
          type: 'success',
          text: this.$t('export-success')
        });

        this.close();
      }
      catch (error) {
        const reportErr = (errorMessage) => {
          this.$notify({
            type: 'error',
            text: errorMessage
          });
        };
        console.error('Export error:', error);
        
        let errorMessage = this.$t('export-error-unexpected');
        const handleErrorBlob = async (blob) => {
          return await new Promise((resolve) => {
            const errorBlob = error.response.data;
            if (errorBlob.type === 'application/json') {
              let fileReader = new FileReader();
              fileReader.onload = () => {
                try {
                  const errorJson = JSON.parse(fileReader.result);
                  resolve(errorJson.detail);
                }
                catch (e) {
                  resolve(errorMessage);
                }
              };
              fileReader.readAsText(blob);
            }
            else {
              resolve(errorMessage);
            }
          });
        };

        handleErrorBlob(error.response.data)
          .then(reportErr);
      }
      finally {
        this.exporting = false;
      }
    }
  },
  async created() {
    await Promise.all([
      this.fetchImages(),
      this.fetchAlgorithmJobs()
    ]);
  }
};
</script>

<style scoped>
.export-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.export-progress {
  text-align: center;
  padding: 2rem;
}

.field {
  margin-bottom: 1rem;
}

.checkbox {
  margin-right: 1rem;
}
</style>
